import { logger } from "@/utils/logger";
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Send, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useVoiceRecording } from '../../hooks/useVoiceRecording';
import { useStateManager } from '../../hooks/useStateManager';
import { useIsMobile } from '../../hooks/use-mobile';
import ListeningInterface from './ListeningInterface';
import { useFormContext } from '../../contexts/FormContext';
import { useCertificationStyles } from '../../hooks/useCertificationStyles';
import { useDealerSession } from '../../contexts/DealerSessionContext';





interface ChatInputProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  onSendMessage: () => void;
  disabled?: boolean;
  isWelcomeScreen?: boolean;
  onCollapseForm?: () => void;
  sessionId: string;
  isFormCollapsed?: boolean;
  waitingVideoData?: {
    Waiting_Youtube_Video_url: string;
    Waiting_Message: string;
  } | null;
  isLoading?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputValue,
  setInputValue,
  onSendMessage,
  disabled = false,
  isWelcomeScreen = false,
  onCollapseForm,
  sessionId,
  isFormCollapsed = false,
  waitingVideoData,
  isLoading = false,
}) => {
  const { styles, isLoading: isLoadingStyles } = useCertificationStyles();
  const { sessionData } = useDealerSession();
  
  // Debug certification styles
  React.useEffect(() => {
    
  }, [styles, isLoadingStyles]);
  const { t, language } = useLanguage();
  const isMobile = useIsMobile();
  const { setActiveForm, activeForm } = useFormContext();
  const { updateState } = useStateManager();

  

  
  // Check if input should be collapsed for appointment forms (but show it when form is collapsed)
  const shouldCollapseInput = (activeForm === 'Appointment' || activeForm === 'Service') && !isFormCollapsed;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showMicTooltip, setShowMicTooltip] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [simulatedTypingEnabled, setSimulatedTypingEnabled] = useState(false);
  const [userHasTyped, setUserHasTyped] = useState(false);
  const [isSimulatedText, setIsSimulatedText] = useState(false);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Simulated prompt typing disabled to prevent keystroke interference
  const [isTyping, setIsTyping] = useState(false);
  const stopTyping = () => setIsTyping(false);

  // Track when text is from simulated typing
  useEffect(() => {
    if (isTyping || (inputValue && !userHasTyped)) {
      setIsSimulatedText(true);
    } else if (userHasTyped) {
      setIsSimulatedText(false);
    }
  }, [isTyping, inputValue, userHasTyped]);

  // Listen for prompt menu clicks to mark input as real user input
  useEffect(() => {
    const handlePromptMenuClick = () => {
      setUserHasTyped(true);
      setIsSimulatedText(false);
    };

    window.addEventListener('promptMenuClick', handlePromptMenuClick);
    
    return () => {
      window.removeEventListener('promptMenuClick', handlePromptMenuClick);
    };
  }, []);

  // Listen for global event to start simulated typing
  useEffect(() => {
    const handleStartSimulatedTyping = () => {
      setSimulatedTypingEnabled(true);
    };

    window.addEventListener('startSimulatedTyping', handleStartSimulatedTyping);
    
    return () => {
      window.removeEventListener('startSimulatedTyping', handleStartSimulatedTyping);
    };
  }, []);

  // Stop simulated typing when a form becomes active or when user has typed
  useEffect(() => {
    if (activeForm || userHasTyped) {
      window.dispatchEvent(new CustomEvent('stopSimulatedTyping'));
    }
  }, [activeForm, userHasTyped]);

  const {
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecording({
    onTranscriptionComplete: (text) => {
      if (!text.trim()) return;
      
      // Mark that user has typed
      setUserHasTyped(true);
      
      // Mark this as a voice message for tracking
      sessionStorage.setItem('d2c_is_voice_message', 'true');
      
      // Set the transcribed text and immediately send
      setInputValue(text);
      
      // Use a minimal delay just for state propagation, then force send
      setTimeout(() => {
        // Trigger form submission directly
        const event = new Event('submit', { bubbles: true, cancelable: true });
        textareaRef.current?.form?.dispatchEvent(event);
      }, 50);
    },
  });

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 72)}px`;
    }
  }, [inputValue]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disabled && inputValue.trim() && !isSimulatedText) {
      // Close voice dictation tip on mobile when message is sent
      if (isMobile) {
        sessionStorage.setItem('d2c_mic_tooltip_shown', 'true');
        setShowMicTooltip(false);
      }
      onSendMessage();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFocus = () => {
    setIsInputFocused(true);
    
    // Stop simulated typing when user focuses input
    if (isTyping) {
      stopTyping();
      window.dispatchEvent(new CustomEvent('stopSimulatedTyping'));
      setInputValue(''); // Clear the input box
      setUserHasTyped(false); // Reset since we're clearing
      setIsSimulatedText(false); // Clear simulated state
    } else if (inputValue && !userHasTyped) {
      // When user focuses on pre-filled text (e.g. auto-skip L1), make it immediately editable
      setUserHasTyped(true);
      setIsSimulatedText(false);
      // Place cursor at end and add space so user can type right after
      if (!inputValue.endsWith(' ') && inputValue.trim()) {
        setInputValue(inputValue + ' ');
      }
    } else {
      // Add space after prompt-generated text if needed
      if (inputValue && !inputValue.endsWith(' ') && inputValue.trim()) {
        setInputValue(inputValue + ' ');
      }
    }
    
    if (isMobile && !showMicTooltip) {
      // Check if tooltip has been shown this session
      const hasShownTooltip = sessionStorage.getItem('d2c_mic_tooltip_shown');
      if (!hasShownTooltip) {
        setShowMicTooltip(true);
        sessionStorage.setItem('d2c_mic_tooltip_shown', 'true');
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }
        tooltipTimeoutRef.current = setTimeout(() => {
          setShowMicTooltip(false);
        }, 4000);
      }
    }
    
    if (onCollapseForm) {
      onCollapseForm();
    }
  };

  const handleBlur = () => {
    setIsInputFocused(false);
  };

  const handleStartRecording = () => {
    // Clear any simulated typing text immediately
    window.dispatchEvent(new CustomEvent('stopSimulatedTyping'));
    setInputValue('');
    setUserHasTyped(false); // Will be set to true when transcription completes
    
    // Mark tooltip as shown since user is using the mic
    sessionStorage.setItem('d2c_mic_tooltip_shown', 'true');
    
    // Use speech-to-text (Whisper) dictation
    startRecording();

    setShowMicTooltip(false);
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
  };



  const handleContactFormOpen = () => {
    setActiveForm('Contact');
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  // Hide the entire input section when forms are active or when showing waiting video
  const shouldHideInput = shouldCollapseInput || (activeForm === 'Offer' && !isFormCollapsed) || (isLoading && waitingVideoData?.Waiting_Youtube_Video_url);
  
  if (shouldHideInput) {
    return null;
  }

  return (
    <>
      <div className="d2cbot-bot_border-t bg-white rounded-b-xl transition-all duration-300">        
        {/* Small "Ask me anything" hint - hide when user has actual input */}
        {(!inputValue || isTyping) && !isInputFocused && (
          <div className="text-left ml-[10px] pt-2 px-4">
            <span className="text-xs text-gray-400 font-light">
              {t('chat.input.hint')}
            </span>
          </div>
        )}
        
        {/* Consistent padding regardless of hint visibility */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-4 pb-3 pt-2" autoComplete="off">
          <div className="flex-1 min-w-0 relative">
            <div className="relative d2cbot-bot_border d2cbot-bot_border-gray-300 rounded-lg flex items-center bg-white min-h-[44px]">
              <textarea
                ref={textareaRef}
                value={isTranscribing ? t('chat.input.transcribing') : inputValue}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setInputValue(newValue);
                  // User has typed if they're changing the value and it's not empty
                  if (newValue.trim()) {
                    setUserHasTyped(true);
                    setIsSimulatedText(false); // User is actually typing now
                  } else {
                    setUserHasTyped(false);
                    setIsSimulatedText(false);
                  }
                }}
                onKeyPress={handleKeyPress}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={isWelcomeScreen ? t('chat.input.placeholder.welcome') : t('chat.input.placeholder')}
                disabled={disabled || isTranscribing}
                className={`flex-1 px-3 py-2 bg-transparent resize-none focus:outline-none max-h-[72px] min-h-[40px] text-base leading-5 overflow-y-auto d2cbot-bot_border-0 placeholder:text-gray-400 ${
                  isSimulatedText ? 'text-gray-400' : 'text-black'
                }`}
                rows={1}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
              
              {/* Contact form button */}
              <Button
                type="button"
                onClick={handleContactFormOpen}
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0 hover:bg-gray-100 flex-shrink-0 mr-2"
                title={t('forms.contact.title')}
                disabled={disabled}
              >
                <FontAwesomeIcon 
                  icon={faEnvelope} 
                  className="fa-envelope-chat"
                />
              </Button>

              {/* Voice recording button with tooltip */}
              <div className="relative ml-1">
                <Button
                  type="button"
                  onClick={handleStartRecording}
                  variant="ghost"
                  size="sm"
                  className={`h-10 w-10 p-0 hover:bg-gray-100 flex-shrink-0 mr-2 transition-all duration-300 ${
                    isMobile && isInputFocused ? 'animate-pulse' : ''
                  }`}
                  title={t('chat.input.start.recording')}
                  disabled={disabled || isTranscribing || isRecording}
                >
                  <FontAwesomeIcon 
                    icon={faMicrophone} 
                    className={`fa-microphone-chat ${isMobile && isInputFocused ? 'focused' : ''}`}
                  />
                </Button>

              </div>
            </div>
          </div>

          {/* Send button */}
          <button
            type="submit"
            disabled={disabled || !inputValue.trim() || isTranscribing || isSimulatedText}
            className="d2cbot-btn-fixed flex-shrink-0 hover:opacity-90 transition-all duration-200 rounded-md flex items-center justify-center"
            style={{
              width: '44px',
              height: '44px',
              minWidth: '44px',
              maxWidth: '44px',
              backgroundColor: styles.header_bg_col || '#000000',
              color: styles.header_text_col || '#ffffff',
              border: `1px solid ${styles.header_bg_col || '#000000'}`,
              boxSizing: 'border-box',
            }}
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>

      {/* Listening Interface Overlay - Speech-to-Text (Whisper) mode */}
      {(isRecording || isTranscribing) && (
        <ListeningInterface
          onCancel={() => cancelRecording()}
          onConfirm={() => stopRecording()}
          isTranscribing={isTranscribing}
        />
      )}

    </>
  );
};

export default ChatInput;